import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { TopoLines } from './TopoLines';
import { PAGE_IMAGES } from '../data/assets';
type Kind = 'gates' | 'bench' | 'decision' | 'verdict' | 'worksheet';
type Page = {
  image: string;
  vol: string;
  tag: string;
  title: string;
  kind: Kind;
  caption: string;
};

/**
 * "Peek inside" — títulos reales del índice aprobado del DPS64.
 * Mientras no haya páginas exportadas (data/assets.ts → PAGE_IMAGES), cada
 * tarjeta muestra una vista previa diseñada del formato de esa página.
 */
const pages: Page[] = [{
  image: PAGE_IMAGES.fourGates,
  vol: '1',
  tag: 'Framework',
  title: 'The 4-Gate Diagnostic Sequence',
  kind: 'gates',
  caption: 'Vol. 1 · The 4 Gates, in the order you test them'
}, {
  image: PAGE_IMAGES.surgeCard,
  vol: '2',
  tag: 'BC-34',
  title: 'Still Surging After a Carb Clean: Hunt the Air Leak',
  kind: 'bench',
  caption: 'Vol. 2 · A Bench Card: steps, PASS/FAIL, what to log'
}, {
  image: PAGE_IMAGES.carbGate,
  vol: '3',
  tag: 'Framework',
  title: 'The Carb Decision Gate',
  kind: 'decision',
  caption: 'Vol. 3 · Adjust, clean, rebuild or replace — with a test'
}, {
  image: PAGE_IMAGES.seatSwitch,
  vol: '4',
  tag: 'BC-104',
  title: 'Test a Seat Switch',
  kind: 'bench',
  caption: 'Vol. 4 · Safety switches tested one by one, never jumped'
}, {
  image: PAGE_IMAGES.compression,
  vol: '5',
  tag: 'Framework',
  title: 'The Compression Verdict Chart',
  kind: 'verdict',
  caption: 'Vol. 5 · Healthy, worn, valve or gasket'
}, {
  image: PAGE_IMAGES.repairOrder,
  vol: '6',
  tag: 'WS-19',
  title: 'Repair Order With Customer Authorization',
  kind: 'worksheet',
  caption: 'Vol. 6 · Printable shop forms, ready for the counter'
}];
function Bar({
  w = 'w-full'
}: {
  w?: string;
}) {
  return <span className={`block h-1.5 rounded-full bg-pine/15 ${w}`} data-id="element-141" data-mp="2zpvul0q4eekq" />;
}
function PreviewBody({
  kind
}: {
  kind: Kind;
}) {
  if (kind === 'gates') {
    const gates = [['1', 'Safety & Electrical', 'bg-patrol-mustard'], ['2', 'Spark', 'bg-ember'], ['3', 'Fuel & Air', 'bg-sky'], ['4', 'Compression', 'bg-moss']];
    return <div className="grid h-full grid-cols-4 gap-1.5" data-id="element-142" data-mp="1hgi2y1qxw3t8">
        {gates.map(([n, label, c]) => <div key={n} className="flex flex-col rounded-md border border-pine/15 bg-white p-1.5" data-id="element-143" data-mp="kqras964esql">
            <span className={`flex h-6 w-6 items-center justify-center rounded-full font-display text-sm text-pine ${c}`} data-id="element-144" data-mp="poanv5r30zgy">
              {n}
            </span>
            <span className="mt-1 font-display text-[10px] uppercase leading-none text-pine" data-id="element-145" data-mp="tt0h0mucvzy6">
              {label}
            </span>
            <span className="mt-auto grid gap-1" data-id="element-146" data-mp="256m5s6wazzj6">
              <Bar data-id="element-147" data-mp="257x7m166ux7n" />
              <Bar w="w-2/3" data-id="element-148" data-mp="2a4xe2d6ya1za" />
              <span className="mt-0.5 rounded-sm bg-moss px-1 text-center text-[7px] font-bold text-white" data-id="element-149" data-mp="2a44ebw6escqn">PASS →</span>
            </span>
          </div>)}
      </div>;
  }
  if (kind === 'bench') {
    const fields = ['Symptom', 'Machines', 'Gate', 'Time', 'Tools', 'Safety'];
    return <div className="grid h-full grid-cols-[38%_1fr] gap-2" data-id="element-150" data-mp="2ahzneeu29j0g">
        <div className="grid content-start gap-1.5 rounded-md border border-pine/15 bg-white p-1.5" data-id="element-151" data-mp="26hfq9b9hmfeb">
          {fields.map(f => <div key={f} data-id="element-152" data-mp="21je8qurpmo4y">
              <span className="block text-[7px] font-bold uppercase tracking-wider text-pine-muted" data-id="element-153" data-mp="21jxoq5xsv2gc">{f}</span>
              <Bar w="w-4/5" data-id="element-154" data-mp="1wlcqud60suv2" />
            </div>)}
        </div>
        <div className="grid content-start gap-1.5" data-id="element-155" data-mp="1rkyieu0ibiwi">
          {[1, 2, 3].map(s => <div key={s} className="flex items-center gap-1.5 rounded-md border border-pine/15 bg-white p-1.5" data-id="element-156" data-mp="1njmz16887twr">
              <span className="flex h-4 w-4 flex-none items-center justify-center rounded-full bg-pine font-display text-[9px] text-paper" data-id="element-157" data-mp="3lwwo4lzlxwnn">{s}</span>
              <span className="flex-1" data-id="element-158" data-mp="3ozt242imhuj9"><Bar data-id="element-159" data-mp="3ozjeumoyq1h9" /></span>
              <span className="rounded-sm bg-moss px-1 text-[7px] font-bold text-white" data-id="element-160" data-mp="3p0ccmy5y1gn9">PASS</span>
              <span className="rounded-sm bg-signal px-1 text-[7px] font-bold text-white" data-id="element-161" data-mp="3tydu5enq17wm">FAIL</span>
            </div>)}
          <div className="rounded-md bg-patrol-mustard/30 p-1.5" data-id="element-162" data-mp="3g1h3pebilsg9">
            <span className="block text-[7px] font-bold uppercase tracking-wider text-pine" data-id="element-163" data-mp="3jz52isgbw5df">Common mistake</span>
            <Bar w="w-3/4" data-id="element-164" data-mp="3680c8gyuopfr" />
          </div>
          <span className="justify-self-end rounded-full border border-ember px-1.5 text-[7px] font-bold uppercase text-ember-deep" data-id="element-165" data-mp="3b0jo99hw3cig">
            Confirm OEM spec
          </span>
        </div>
      </div>;
  }
  if (kind === 'decision') {
    const options = [['Adjust', 'bg-moss'], ['Clean', 'bg-sky'], ['Rebuild', 'bg-patrol-mustard'], ['Replace', 'bg-signal']];
    return <div className="flex h-full flex-col gap-1.5" data-id="element-166" data-mp="2s9tklikhvloj">
        <div className="mx-auto rounded-md border-2 border-pine bg-white px-2 py-1 font-display text-[10px] uppercase text-pine" data-id="element-167" data-mp="2sbmwpddvmy3x">
          Fuel path proven OK?
        </div>
        <span className="mx-auto h-3 border-l-2 border-dashed border-pine/40" data-id="element-168" data-mp="23q8drec2ysf6" />
        <div className="grid flex-1 grid-cols-4 gap-1.5" data-id="element-169" data-mp="2hphqg412v4ee">
          {options.map(([o, c]) => <div key={o} className="flex flex-col rounded-md border border-pine/15 bg-white p-1.5" data-id="element-170" data-mp="2dls4lmbsf0x2">
              <span className={`rounded-sm px-1 text-center font-display text-[10px] uppercase text-pine ${c}`} data-id="element-171" data-mp="2rlkvbhnbdh51">{o}</span>
              <span className="mt-1.5 grid gap-1" data-id="element-172" data-mp="2mn01bzynntp1">
                <Bar data-id="element-173" data-mp="31cxm05xdm19h" />
                <Bar w="w-3/4" data-id="element-174" data-mp="31be4yd3wbm6n" />
                <Bar w="w-1/2" data-id="element-175" data-mp="31eig5ufs3i6t" />
              </span>
            </div>)}
        </div>
      </div>;
  }
  if (kind === 'verdict') {
    const rows = [['Healthy', 'bg-moss'], ['Worn', 'bg-patrol-mustard'], ['Valve', 'bg-ember'], ['Gasket', 'bg-signal']];
    return <div className="grid h-full content-start gap-1.5" data-id="element-176" data-mp="35c6e76crpr2x">
        {rows.map(([r, c]) => <div key={r} className="grid grid-cols-[22%_1fr_1fr] items-center gap-1.5 rounded-md border border-pine/15 bg-white p-1.5" data-id="element-177" data-mp="22yibkvjrvdks">
            <span className="flex items-center gap-1 font-display text-[10px] uppercase text-pine" data-id="element-178" data-mp="2c0rjrerkiuv0">
              <span className={`h-2.5 w-2.5 rounded-full ${c}`} data-id="element-179" data-mp="2gsjdfy60tgpd" />
              {r}
            </span>
            <Bar data-id="element-180" data-mp="2lrm8qqw788k5" />
            <Bar w="w-2/3" data-id="element-181" data-mp="2lq1dt05z2nlr" />
          </div>)}
      </div>;
  }
  const lines = ['Customer', 'Machine & model no.', 'Complaint', 'Cause', 'Correction', 'Authorization'];
  return <div className="grid h-full content-start gap-1.5 rounded-md border border-pine/15 bg-white p-2" data-id="element-182" data-mp="2vp0oeoaastbe">
      {lines.map(l => <div key={l} className="flex items-end gap-1.5" data-id="element-183" data-mp="2zqc5u6j0q88i">
          <span className="w-[38%] text-[7px] font-bold uppercase tracking-wider text-pine-muted" data-id="element-184" data-mp="11br7ibffa5j4">{l}</span>
          <span className="flex-1 border-b border-dashed border-pine/30" data-id="element-185" data-mp="15c2uhuv9chxr" />
        </div>)}
    </div>;
}
function PagePreview({
  page
}: {
  page: Page;
}) {
  return <div className="topo-light absolute inset-0 flex flex-col bg-paper p-3" data-id="element-186" data-mp="t2lg9x80tgrq">
      <div className="flex items-center justify-between" data-id="element-187" data-mp="t0ikewk6stf3">
        <span className="rounded-sm bg-ember px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider text-pine" data-id="element-188" data-mp="o4jzxk1zbvjy">
          {page.tag}
        </span>
        <span className="text-[8px] font-bold uppercase tracking-widest text-pine-muted" data-id="element-189" data-mp="1gs7h0p41k41u">
          Volume {page.vol}
        </span>
      </div>
      <p className="mt-1.5 font-display text-[15px] uppercase leading-[1.02] text-pine" data-id="element-190" data-mp="1bu5zw9q6eet0">
        {page.title}
      </p>
      <div className="mt-2 min-h-0 flex-1" data-id="element-191" data-mp="17py823of8iqk">
        <PreviewBody kind={page.kind} data-id="element-192" data-mp="12sokyuoi8j5w" />
      </div>
    </div>;
}
export function LookInside() {
  const trackRef = useRef<HTMLUListElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const updateArrows = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);
  useEffect(() => {
    updateArrows();
    window.addEventListener('resize', updateArrows);
    return () => window.removeEventListener('resize', updateArrows);
  }, [updateArrows]);
  const scrollByCard = (direction: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector('li');
    const step = card ? card.getBoundingClientRect().width + 16 : el.clientWidth * 0.8;
    el.scrollBy({
      left: step * direction,
      behavior: 'smooth'
    });
  };
  const arrowClass = 'absolute top-[42%] z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-paper-dark bg-white text-pine shadow-card transition-[opacity,background-color,transform] duration-150 ease-out hover:bg-paper active:scale-95 focus:outline-none focus-visible:ring-4 focus-visible:ring-ember/50 disabled:pointer-events-none disabled:opacity-0';
  return <section aria-labelledby="peek-heading" className="topo-light w-full bg-paper" data-id="element-193" data-mp="mfb1ghbod9vx">
      <div className="mx-auto max-w-6xl px-5 pt-14 sm:pt-20" data-id="element-194" data-mp="hiatg0v9lx26">
        <div className="text-center" data-id="element-195" data-mp="v8wffyg0mn4a">
          <p className="eyebrow text-ember-deep" data-id="element-196" data-mp="v7ur4s9zo44m">A peek inside</p>
          <h2 id="peek-heading" className="trail-underline mt-3 font-display text-[34px] uppercase leading-tight text-pine sm:text-6xl" data-id="element-197" data-mp="r7aya0wg7moa">
            Built for the bench, not the bookshelf
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-pine-soft" data-id="element-198" data-mp="3up2tdo5vsmqp">
            Landscape pages you print and keep at the bench. Every Bench Card
            has the same layout: symptom, machines, gate, time, tools, safety,
            numbered steps with PASS/FAIL, what to do if it fails, the most
            common mistake and what to write on the repair order.
          </p>
        </div>

        <div className="relative mt-8" data-id="element-199" data-mp="1ixgt1nhqwxgb">
          <TopoLines className="pointer-events-none absolute -left-10 -top-6 hidden h-40 w-40 text-bark/10 lg:block" data-id="element-200" data-mp="1j0usonz0ysk2" />
          <button type="button" onClick={() => scrollByCard(-1)} disabled={!canPrev} aria-label="Previous page" className={`${arrowClass} left-0 sm:-left-2`} data-id="element-201" data-mp="32507ijyn0ozc">
            <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" data-id="element-202" data-mp="2tbdgf75dk9wh" />
          </button>
          <button type="button" onClick={() => scrollByCard(1)} disabled={!canNext} aria-label="Next page" className={`${arrowClass} right-0 sm:-right-2`} data-id="element-203" data-mp="2odlnqawwojr8">
            <ChevronRightIcon className="h-6 w-6" aria-hidden="true" data-id="element-204" data-mp="2efo11rup2v0q" />
          </button>
          <ul ref={trackRef} onScroll={updateArrows} className="-mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-5 [scrollbar-width:thin]" data-id="element-205" data-mp="2aevr0we00m3e">
            {pages.map(page => <li key={page.title} className="shrink-0 basis-[84%] snap-start sm:basis-[340px]" data-id="element-206" data-mp="8kcxsub87gco">
                <figure className="overflow-hidden rounded-2xl border border-paper-dark bg-white shadow-card" data-id="element-207" data-mp="1vi40c548pys9">
                  <div className="relative aspect-[4/3] w-full overflow-hidden bg-paper-dark" data-id="element-208" data-mp="20ax5gu3mf94v">
                    {page.image ? <img src={page.image} alt={`${page.title} page`} loading="lazy" className="absolute inset-0 h-full w-full object-cover object-top" data-id="element-209" data-mp="24dbmd8nqbyti" /> : <PagePreview page={page} data-id="element-210" data-mp="1gnr64xiynfnx" />}
                  </div>
                  <figcaption className="px-3.5 py-3 text-[13px] font-semibold leading-snug text-pine" data-id="element-211" data-mp="1lm0xex1kdw49">
                    {page.caption}
                  </figcaption>
                </figure>
              </li>)}
          </ul>
          <p className="field-note mt-1 text-center text-xl uppercase text-pine-soft sm:text-2xl" data-id="element-212" data-mp="wx3xi20zf8fx">
            161 Bench Cards · 31 worksheets · a field card in every volume
          </p>
        </div>
      </div>
    </section>;
}